import {
  DestroyRef,
  Directive,
  ElementRef,
  Injectable,
  PLATFORM_ID,
  inject,
  input,
  numberAttribute,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Margen inferior negativo: el elemento se revela algo antes del borde. */
const ROOT_MARGIN = '0px 0px -12% 0px';
const REVEALED_CLASS = 'is-in';

/**
 * Un unico IntersectionObserver para toda la pagina, no uno por elemento.
 * La landing revela decenas de nodos; instanciar un observer por cada uno
 * multiplica el trabajo del hilo principal justo mientras el usuario hace
 * scroll, que es cuando menos margen hay.
 */
@Injectable({ providedIn: 'root' })
export class RevealObserver {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private observer: IntersectionObserver | null = null;

  /**
   * `false` cuando no hay que animar nada: SSR, navegador sin
   * IntersectionObserver, o el script inline de index.html no llego a marcar
   * `js-motion` (JS deshabilitado o roto). En esos casos el contenido debe
   * quedarse en su estado final, visible.
   */
  get enabled(): boolean {
    return (
      this.isBrowser &&
      typeof IntersectionObserver !== 'undefined' &&
      document.documentElement.classList.contains('js-motion')
    );
  }

  observe(element: Element): void {
    if (!this.enabled) return;
    this.ensureObserver().observe(element);
  }

  unobserve(element: Element): void {
    this.observer?.unobserve(element);
  }

  private ensureObserver(): IntersectionObserver {
    this.observer ??= new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add(REVEALED_CLASS);
          // Revelar es de ida: una vez visto, deja de costar.
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: ROOT_MARGIN, threshold: 0.12 },
    );

    return this.observer;
  }
}

/**
 * Marca un elemento para que se revele al entrar en pantalla.
 *
 * El elemento arranca oculto solo si `html.js-motion` esta presente (ver
 * `_motion.scss`), asi que sin JS la pagina se lee entera. `lcRevealDelay`
 * escalona grupos: un retardo en ms aplicado como `transition-delay`.
 */
@Directive({
  selector: '[lcReveal]',
  host: { '[style.--reveal-delay.ms]': 'delay()' },
})
export class RevealDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly reveal = inject(RevealObserver);

  readonly delay = input(0, { alias: 'lcRevealDelay', transform: numberAttribute });

  constructor() {
    const element = this.host.nativeElement;
    this.reveal.observe(element);
    inject(DestroyRef).onDestroy(() => this.reveal.unobserve(element));
  }
}
